import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { take } from 'rxjs/operators';
import { GridDataResult, DataStateChangeEvent, GridComponent, RowClassArgs } from '@progress/kendo-angular-grid';
import { State, orderBy, process, CompositeFilterDescriptor, filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import { EmployeeService } from '../../services/employees.service';
import { NotificationService } from '../../services/notification.service';
import { OrderService } from '../../services/order.service';
import { UtilService } from '../../services/util.service';
import {
    Employee,
    CrossBorderInTransitLoad
} from '../../models/models';

@Component({
    selector: 'cross-border-grid',
    templateUrl: './cross-border-grid.component.html',
    standalone: false
})
export class CrossBorderGrid implements OnInit {
    @ViewChild(GridComponent, { static: false }) public crossBorderGrid!: GridComponent;

    public crossBorderLoadList: CrossBorderInTransitLoad[] = [];
    public crossBorderLoad: CrossBorderInTransitLoad = {} as CrossBorderInTransitLoad;
    public allEmployees: Employee[] = [];
    public logisticSpecialists: Employee[] = [];
    public countries: string[] = [];
    public c2cList: string[] = [];
    public gridData: GridDataResult = { data: [], total: 0 };
    public gridState: State = { skip: 0, take: 20, sort: [{ dir: 'desc', field: 'quoteOrderID' }] };
    private logisticSpecialistsFilter: any[] = [];
    private originCountryFilter: any[] = [];
    private destinationCountryFilter: any[] = [];
    private c2cFilter: any[] = [];

    constructor(private notificationService: NotificationService,
        private orderService: OrderService,
        private titleService: Title,
        private employeeService: EmployeeService,
        private utilService: UtilService,
        private ngZone: NgZone) {
        this.titleService.setTitle('Border Crossing');
        this.getActiveEmployees();
    }

    ngOnInit() {
        this.initFields();
        this.getLogisticSpecialists();
        this.getCrossBorderLoads();
    }

    private initFields() {
        this.utilService.isLoading = false;
    }

    private getLogisticSpecialists() {
        this.employeeService.getEmployeesByGroupName("logistic specialists").subscribe(result => {
            this.logisticSpecialists = result;
        });
    }

    private getCountries() {
        this.countries.length = 0;
        for (var i = 0; i < this.crossBorderLoadList.length; i++) {
            var originCountry = this.crossBorderLoadList[i].shipperCountryAbbrev;
            var destinationCountry = this.crossBorderLoadList[i].consigneeCountryAbbrev;
            if (this.countries.indexOf(originCountry) < 0) {
                this.countries.push(originCountry);
            }
            if (this.countries.indexOf(destinationCountry) < 0) {
                this.countries.push(destinationCountry);
            }
        }
        //console.log("getCountries", this.countries);
    }

    private getC2CList() {
        this.c2cList.length = 0;
        for (var i = 0; i < this.crossBorderLoadList.length; i++) {
            var c2c = this.crossBorderLoadList[i].legCntryToCntry;
            if (this.c2cList.indexOf(c2c) < 0) {
                this.c2cList.push(c2c);
            }
        }
        //console.log("getC2CList", this.c2cList);
    }

    public getOrderNumber() {
        if (this.crossBorderLoad.orderNumber != undefined) {
            return this.crossBorderLoad.orderNumber + " - " + this.crossBorderLoad.legNumber;
        }
        else {
            return "";
        }
    }

    private getCrossBorderLoads() {
        // Clear the orders list for data refresh
        this.crossBorderLoadList.length = 0;

        //this.gridState.sort = [
        //    {
        //        field: "orderNumber",
        //        dir: "desc"
        //    },
        //    {
        //        field: "trackingPriority",
        //        dir: "asc"
        //    },
        //    {
        //        field: "orderEventDateUTC",
        //        dir: "asc"
        //    },
        //    {
        //        field: "orderEventSequence",
        //        dir: "asc"
        //    }
        //];

        this.utilService.isLoading = true;
        this.orderService.getCrossBorderInTransitLoads().subscribe(result => {
            var loads: CrossBorderInTransitLoad[] = result.resultCollection.sort((left, right): number => {
                if (left.quoteOrderID < right.quoteOrderID) return -1;
                if (left.quoteOrderID > right.quoteOrderID) return 1;
                return 0;
            });

            loads.forEach(s => {
                s.crossingDate = new Date(s.crossingDate);
                s.pickupDate = new Date(s.pickupDate);
                s.deliveryDate = new Date(s.deliveryDate);
                //if (s.orderEventDateUTC != null) {
                //    s.orderEventDateUTC = new Date(s.orderEventDateUTC + "Z");
                //}
                //else {
                //    s.orderEventDateUTC = s.orderEventDate;
                //}
            });

            this.crossBorderLoadList = Object.assign([], loads);

            //this.trackingBoardNextEvents = Object.assign([], this.trackingBoard);
            this.getCountries();
            this.getC2CList();
            this.utilService.isLoading = false;
            this.setGrid();
        });
    }

    public viewOrder(order: CrossBorderInTransitLoad) {
        this.crossBorderLoad = Object.assign({}, order);
    }

    public cancelView() {
        this.crossBorderLoad = {} as CrossBorderInTransitLoad;
        this.getCrossBorderLoads();
    }

    private setGrid() {
        var load = this.crossBorderLoadList.slice();

        if (this.gridState.filter) load = filterBy(load, this.gridState.filter);
        if (this.gridState.sort) load = orderBy(load, this.gridState.sort);

        this.gridData = process(load, this.gridState);
        this.fitColumns();
        //console.log("setGrid", load, this.gridData);
    }

    public filterChange(filter: CompositeFilterDescriptor): void {
        //console.log("this.gridState.filter", this.gridState.filter, this.carrierRepsFilter);
        this.logisticSpecialistsChange(this.logisticSpecialistsFilter);
        this.countryChange(this.originCountryFilter, "origin");
        this.countryChange(this.destinationCountryFilter, "destination");
        this.c2cChange(this.c2cFilter);
        this.setGrid();
    }

    private fitColumns() {
        this.ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => {
            if (this.crossBorderGrid == null) return;
            this.crossBorderGrid.autoFitColumns();
        });
    }

    public logisticSpecialistsChange(values: any[]): void {
        //console.log("logisticSpecialistsChange", values);
        this.logisticSpecialistsFilter = values;
        var valMap = values.map(value => ({
            field: "logisticSpecialist",
            operator: "eq",
            value
        }));
        var cf: CompositeFilterDescriptor = {
            filters: valMap,
            logic: "or"
        }

        if (!this.gridState.filter) {
            this.gridState.filter = {
                filters: [
                    {
                        filters: valMap,
                        logic: "or"
                    }
                ],
                logic: "and"
            };
        }
        else {
            var currFilters = this.gridState.filter.filters;
            //console.log("logisticSpecialistsChange:currFilters before", currFilters);
            var idx = -1;
            currFilters.forEach(function (f: CompositeFilterDescriptor | FilterDescriptor, index) {
                var cf = f as CompositeFilterDescriptor;
                if (cf.filters) {
                    var fd = cf.filters[0] as FilterDescriptor;
                    if (fd.field == "logisticSpecialist") {
                        idx = index;
                    }
                    //console.log("logisticSpecialistsChange:ComponsiteFilterDescriptor", cf.filters);
                }
            });
            // Remove the componsite filter if no other filters within the composite filter are in place
            if (idx >= 0) {
                currFilters.splice(idx, 1);
            }
            // Add the composite filter if value(s) selected
            if (values.length > 0) {
                currFilters.push(cf);
            }
            //console.log("logisticSpecialistsChange:currFilters after", currFilters);
        }

        //console.log("logisticSpecialistsChange:this.gridState.filter", this.gridState.filter, values, this.logisticSpecialistsFilter);
        this.setGrid();
    }

    public countryChange(values: any[], type: string): void {
        //console.log("countryChange", values, type);
        var field: string = "";
        if (type == "origin") {
            this.originCountryFilter = values;
            field = "originCountryAbbreviation";
        }
        else if (type == "destination") {
            this.destinationCountryFilter = values;
            field = "destinationCountryAbbreviation";
        }
        var valMap = values.map(value => ({
            field: field,
            operator: "eq",
            value
        }));
        var cf: CompositeFilterDescriptor = {
            filters: valMap,
            logic: "or"
        }

        if (!this.gridState.filter) {
            this.gridState.filter = {
                filters: [
                    {
                        filters: valMap,
                        logic: "or"
                    }
                ],
                logic: "and"
            };
        }
        else {
            var currFilters = this.gridState.filter.filters;
            //console.log("countryChange:currFilters before", currFilters);
            var idx = -1;
            currFilters.forEach(function (f: CompositeFilterDescriptor | FilterDescriptor, index) {
                var cf = f as CompositeFilterDescriptor;
                if (cf.filters) {
                    //idx = index;
                    var fd = cf.filters[0] as FilterDescriptor;
                    if (fd.field == field) {
                        idx = index;
                    }
                    //console.log("countryChange:ComponsiteFilterDescriptor", cf.filters, field, idx);
                }
            });
            // Remove the componsite filter if no other filters within the composite filter are in place
            if (idx >= 0) {
                currFilters.splice(idx, 1);
            }
            // Add the composite filter if value(s) selected
            if (values.length > 0) {
                currFilters.push(cf);
            }
            //console.log("countryChange:currFilters after", currFilters);
        }

        //console.log("countryChange:this.gridState.filter", this.gridState.filter, values, this.originCountryFilter);
        this.setGrid();
    }

    public c2cChange(values: any[]): void {
        //console.log("countryChange", values, type);
        var field: string = "legCntryToCntry";
        this.c2cFilter = values;
        var valMap = values.map(value => ({
            field: "legCntryToCntry",
            operator: "eq",
            value
        }));
        var cf: CompositeFilterDescriptor = {
            filters: valMap,
            logic: "or"
        }

        if (!this.gridState.filter) {
            this.gridState.filter = {
                filters: [
                    {
                        filters: valMap,
                        logic: "or"
                    }
                ],
                logic: "and"
            };
        }
        else {
            var currFilters = this.gridState.filter.filters;
            //console.log("countryChange:currFilters before", currFilters);
            var idx = -1;
            currFilters.forEach(function (f: CompositeFilterDescriptor | FilterDescriptor, index) {
                var cf = f as CompositeFilterDescriptor;
                if (cf.filters) {
                    //idx = index;
                    var fd = cf.filters[0] as FilterDescriptor;
                    if (fd.field == field) {
                        idx = index;
                    }
                    //console.log("countryChange:ComponsiteFilterDescriptor", cf.filters, field, idx);
                }
            });
            // Remove the componsite filter if no other filters within the composite filter are in place
            if (idx >= 0) {
                currFilters.splice(idx, 1);
            }
            // Add the composite filter if value(s) selected
            if (values.length > 0) {
                currFilters.push(cf);
            }
            //console.log("countryChange:currFilters after", currFilters);
        }

        //console.log("countryChange:this.gridState.filter", this.gridState.filter, values, this.originCountryFilter);
        this.setGrid();
    }

    public logisticSpecialistsFilters(filter: CompositeFilterDescriptor): FilterDescriptor[] {
        //this.logisticSpecialistsFilter.splice(0, this.logisticSpecialistsFilter.length, ...flatten(filter).map(({ value }) => value));
        //console.log("logisticSpecialistsFilters", filter, this.logisticSpecialistsFilter);
        return this.logisticSpecialistsFilter;
    }

    public countryFilters(filter: CompositeFilterDescriptor, type: string): FilterDescriptor[] {
        if (type == "origin") {
            return this.originCountryFilter;
        }
        else if (type == "destination") {
            return this.destinationCountryFilter;
        }
        else {
            return [];
        }
    }

    public c2cFilters(filter: CompositeFilterDescriptor): FilterDescriptor[] {
        return this.c2cFilter;
    }

    private getActiveEmployees() {
        this.employeeService.getActiveEmployees().subscribe(result => {
            this.allEmployees = result;
        });
    }

    public gridStateChange(state: DataStateChangeEvent) {
        this.gridState = state;
        this.gridState.filter = state.filter;
        //console.log("gridStateChange", state.filter);
        this.setGrid();
    }

    public rowCallback(context: RowClassArgs) {
        const isHighPriority = (context.dataItem.loadPriorityAbbreviation == "High");
        const isExpedite = (context.dataItem.rateIndex == "Expedite");
        if (isExpedite) {
            return {
                expedite: isExpedite
            }
        }
        else if (isHighPriority) {
            return {
                highPriority: isHighPriority
            }
        }
    }

    public rowSelected(e: any) {
        //console.log("rowSelected", e.selectedRows);
        this.viewOrder(e.selectedRows[0].dataItem);
    }
}
